<template>
  <v-container class="login">
    <!-- META-DATA -->
    <vue-headful
      :title="'Log in' + this.$store.state.app.meta.titleSuffix"
      :description="null" />

    <v-alert
      type="error"
      outline
      :value="errorMessage"
      transition="slide-y-transition">
      {{ errorMessage }}
    </v-alert>

    <div
      v-if="loadingVerify"
      class="verify-loader">
      <v-progress-circular
        size="100"
        indeterminate
        color="primary" />
      <h3>verifying email address...</h3>
    </div>

    <v-form
      v-if="!loadingVerify"
      ref="login"
      v-model="valid"
      lazy-validation
      class="login-form">
      <v-alert
        :value="accountVerified"
        outline
        type="success"
        class="account-verified">
        Account verified!
      </v-alert>

      <div class="login-form__header">
        <h2>log in</h2>
        <v-btn
          :to="{ name: 'create-account' }"
          flat
          small
          color="primary"
          class="create-account-button">
          create account
        </v-btn>
      </div>

      <v-text-field
        ref="email"
        v-model="emailAddress"
        label="Email Address"
        :rules="[
          v => !!v || 'This field is required',
          v => /.+@.+/.test(v) || 'Email must be valid'
        ]"
        class="login-form__email"
        required
        @keyup.enter.prevent="login" />
      <v-text-field
        ref="password"
        v-model="password"
        label="Password"
        :append-icon="showPassword ? 'visibility_off' : 'visibility'"
        :type="showPassword ? 'text' : 'password'"
        class="login-form__password"
        required
        @click:append="showPassword = !showPassword"
        @keyup.enter.prevent="login" />

      <v-btn
        :disabled="!valid"
        :loading="loading"
        color="primary"
        large
        class="login-button"
        @click="login">
        log in
      </v-btn>
      <v-dialog
        v-model="resetPasswordDialog"
        width="550">
        <v-btn
          slot="activator"
          flat
          small
          class="reset-password-button">
          reset password
        </v-btn>

        <v-card class="reset-password">
          <div class="reset-password__header">
            <h3>reset password</h3>
            <v-btn
              flat
              icon
              class="reset-password__close-dialog"
              @click="resetPasswordDialog = false">
              <v-icon>close</v-icon>
            </v-btn>
          </div>
          <p>
            Please enter the email address you used to create your account and we will send you a link to reset your password.
          </p>
          <v-text-field
            ref="resetPasswordEmail"
            v-model="emailAddress"
            label="Email Address"
            :rules="[
              v => !!v || 'This field is required',
              v => /.+@.+/.test(v) || 'Email must be valid'
            ]"
            class="reset-password__email-field"
            required />
          <v-btn
            :disabled="!emailAddress || !/.+@.+/.test(emailAddress)"
            color="primary"
            class="reset-password__submit-button"
            @click="resetPassword">
            reset password
          </v-btn>
        </v-card>
      </v-dialog>
    </v-form>
  </v-container>
</template>

<script>
import api from '../../utils/api';

export default {
  name: 'Login',
  data() {
    return {
      showPassword: false,
      emailAddress: '',
      password: '',

      valid: false,
      loading: false,
      resetPasswordDialog: false,

      loadingVerify: false,
      accountVerified: null,

      errorMessage: '',
    };
  },
  created() {
    const { token } = this.$route.params;
    if (token) {
      this.loadingVerify = true;
      api
        .getData(`/customers/confirm_email/${token}`)
        .then((res) => {
          this.loadingVerify = false;
          this.accountVerified = true;
          this.emailAddress = res.data.email;
        })
        .catch((error) => {
          this.loadingVerify = false;
          this.accountVerified = false;
          this.errorMessage = error;
        });
    }
  },
  methods: {
    login() {
      if (this.$refs.login.validate()) {
        this.loading = true;
        const email = this.emailAddress;
        const { password } = this;

        this.$store
          .dispatch('auth/login', { email, password })
          .then(() => {
            this.loading = false;
            this.$router.push(this.$route.query.redirect || '/');
          })
          .catch((error) => {
            if (error === 'Customer is not verified') {
              this.errorMessage = 'This email address has not been verified.';
              this.password = '';
            } else if (error) {
              this.errorMessage = 'Incorrect email address or password';
              this.password = '';
            } else if (error.response.status.toString()[0] === '4') {
              this.errorMessage = 'Incorrect email address or password';
              this.password = '';
            } else if (error.response.status.toString()[0] === '5') {
              this.errorMessage = 'Server error: please try again later';
              this.password = '';
            }
            this.loading = false;
          });
      }
    },
    resetPassword() {
      const requestData = {
        email: this.emailAddress,
      };
      api
        .postData('/auth/reset', requestData)
        .then(() => {
          // SHOW SOME SUCCESS MESSAGE:
          // 'email sent' or something
          this.resetPasswordDialog = false;
        });
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.verify-loader
  display: flex
  flex-flow: column
  align-items: center
  justify-content: center
  padding: $base-padding*4

.login
  display: flex
  flex-flow: column nowrap
  align-items: center

.login-form
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: flex-start
  width: 36rem
  max-width: 100%
  padding: $base-padding
  &__header
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    width: 100%
    border-bottom: 1px solid $accent
  &__email
    width: 100%
    margin-top: $base-padding*2
  &__password
    width: 100%

.create-account-button
  margin: 0

.login-button
  width: 100%
  margin-top: $base-padding*2

.reset-password-button
  font-size: $font-size--small

.reset-password
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: flex-start
  padding: $base-padding*2
  text-align: center
  &__header
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    width: 100%
    border-bottom: 1px solid $accent
    margin-bottom: $base-padding*2
  &__close-dialog
    margin-right: 0
  &__email-field
    width: 100%
  &__submit-button
    width: 100%
    margin-top: $base-padding*2
    margin-bottom: 0
</style>
